import { useMemo, useState } from "react";
import { Send, Loader2 } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { useContent } from "@/context/ContentContext";
import { trackEvent } from "@/lib/analytics";
import { validatePersonName, validateMessageText, PHONE_PATTERN } from "@/lib/validation";
import { WORLD_COUNTRIES } from "@/lib/countries";

// Keep these two lists in sync with backend/server.py's QUALIFICATION_OPTIONS /
// TUITION_BUDGET_OPTIONS — the backend rejects anything outside these exact strings.
const QUALIFICATION_OPTIONS = [
  "Secondary School (WAEC/NECO/A-levels/IGCSE or equivalent)",
  "Undergraduate Degree",
  "Postgraduate Degree",
  "Other",
];

const TUITION_BUDGET_OPTIONS = [
  "Under €5,000 per year",
  "€5,000 - €10,000 per year",
  "€10,000 - €20,000 per year",
  "€20,000+ per year",
  "Not Sure Yet",
];

// A rolling window of upcoming Spring/Fall intakes — matches backend's INTAKE_RE
// (`^(Spring|Fall) \d{4}$`), so no hardcoded year ever goes stale.
function buildIntakeOptions() {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth() + 1;
  const options = [];
  for (let y = year; y <= year + 2; y++) {
    if (!(y === year && month > 4)) options.push(`Spring ${y}`);
    if (!(y === year && month > 10)) options.push(`Fall ${y}`);
  }
  return options;
}

export default function AdLeadForm() {
  const { content } = useContent();
  const [params] = useSearchParams();
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    country_of_residence: "",
    highest_qualification: "",
    field_of_study: "",
    destination_country: "",
    preferred_intake: "",
    tuition_budget: "",
    website: "", // honeypot — left blank by real visitors, must stay out of tab order/view
  });

  // Ad platforms append their own tracking params (Meta: utm_source, or just the
  // referring domain) — capture whichever is present so admins can see which
  // campaign a lead came from.
  const source = params.get("utm_source") || params.get("source") || (typeof document !== "undefined" ? document.referrer : "") || "";

  const destinations = [
    ...(content?.countries_primary || []),
    ...(content?.countries_secondary || []),
  ];

  const intakeOptions = useMemo(() => buildIntakeOptions(), []);

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (form.website.trim()) return; // honeypot filled — a bot, not a person; drop silently

    const fullName = `${form.firstName} ${form.lastName}`.trim();
    const nameError = validatePersonName(fullName);
    if (nameError) {
      toast.error(nameError);
      return;
    }
    if (form.phone.trim() && !PHONE_PATTERN.test(form.phone.trim())) {
      toast.error("Please enter a valid WhatsApp phone number (digits only).");
      return;
    }
    const fieldOfStudyError = validateMessageText(form.field_of_study);
    if (fieldOfStudyError) {
      toast.error(fieldOfStudyError);
      return;
    }
    if (!form.country_of_residence || !form.highest_qualification || !form.field_of_study.trim()
      || !form.destination_country || !form.preferred_intake || !form.tuition_budget) {
      toast.error("Please fill in all required fields.");
      return;
    }

    setSubmitting(true);
    try {
      await api.post("/ad-leads", {
        name: fullName,
        email: form.email,
        phone: form.phone || null,
        country_of_residence: form.country_of_residence,
        highest_qualification: form.highest_qualification,
        field_of_study: form.field_of_study,
        destination_country: form.destination_country,
        preferred_intake: form.preferred_intake,
        tuition_budget: form.tuition_budget,
        website: form.website,
        source: source || null,
      });
      trackEvent("generate_lead", {
        method: "ad_landing_page",
        destination_country: form.destination_country || undefined,
        preferred_intake: form.preferred_intake || undefined,
      });
      setSubmitted(true);
    } catch (err) {
      if (err.response?.status === 429) {
        toast.error("You're submitting too quickly — please wait a moment and try again.");
        return;
      }
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Something went wrong — please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="text-center py-8" data-testid="ad-lead-success">
        <p className="text-white font-display text-2xl font-bold mb-2">Thank you!</p>
        <p className="text-white/65">We've received your details and will be in touch shortly with a plan tailored to you.</p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="ad-lead-form">
      {/* Honeypot: invisible to real visitors, off-screen (not display:none, which some
          bots detect and skip) and out of tab order — bots that auto-fill every field trip it. */}
      <div style={{ position: "absolute", left: "-9999px", top: "-9999px" }} aria-hidden="true">
        <label htmlFor="ad-lead-website">Website</label>
        <input
          id="ad-lead-website"
          name="website"
          type="text"
          tabIndex={-1}
          autoComplete="off"
          value={form.website}
          onChange={upd("website")}
        />
      </div>

      <div>
        <label className="text-xs uppercase tracking-widest text-white/55 mb-1.5 block">Name *</label>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <input required placeholder="First" value={form.firstName} onChange={upd("firstName")} className="input-dark" data-testid="ad-lead-first-name" />
          <input required placeholder="Last" value={form.lastName} onChange={upd("lastName")} className="input-dark" data-testid="ad-lead-last-name" />
        </div>
      </div>

      <div>
        <label className="text-xs uppercase tracking-widest text-white/55 mb-1.5 block">Email Address *</label>
        <input required type="email" value={form.email} onChange={upd("email")} className="input-dark" data-testid="ad-lead-email" />
      </div>

      <div>
        <label className="text-xs uppercase tracking-widest text-white/55 mb-1.5 block">WhatsApp Phone Number</label>
        <input
          type="tel"
          inputMode="tel"
          pattern="[0-9+()\-.\s]{6,30}"
          maxLength={30}
          placeholder="Enter WhatsApp phone number"
          value={form.phone}
          onChange={upd("phone")}
          className="input-dark"
          data-testid="ad-lead-phone"
        />
      </div>

      <div>
        <label className="text-xs uppercase tracking-widest text-white/55 mb-1.5 block">Country of Residence *</label>
        <select required value={form.country_of_residence} onChange={upd("country_of_residence")} className="input-dark" data-testid="ad-lead-residence">
          <option value="">— Select —</option>
          {WORLD_COUNTRIES.map((c) => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>
      </div>

      <div>
        <label className="text-xs uppercase tracking-widest text-white/55 mb-1.5 block">Highest Qualification *</label>
        <select required value={form.highest_qualification} onChange={upd("highest_qualification")} className="input-dark" data-testid="ad-lead-qualification">
          <option value="">— Select —</option>
          {QUALIFICATION_OPTIONS.map((q) => (
            <option key={q} value={q}>{q}</option>
          ))}
        </select>
      </div>

      <div>
        <label className="text-xs uppercase tracking-widest text-white/55 mb-1.5 block">Field of Study You're Interested In *</label>
        <input required value={form.field_of_study} onChange={upd("field_of_study")} className="input-dark" data-testid="ad-lead-field-of-study" />
      </div>

      <div>
        <label className="text-xs uppercase tracking-widest text-white/55 mb-1.5 block">Preferred Destination *</label>
        <select required value={form.destination_country} onChange={upd("destination_country")} className="input-dark" data-testid="ad-lead-destination">
          <option value="">— Select —</option>
          {destinations.map((c) => (
            <option key={c.code} value={c.code}>{c.name}</option>
          ))}
        </select>
      </div>

      <div>
        <label className="text-xs uppercase tracking-widest text-white/55 mb-1.5 block">Preferred Intake *</label>
        <select required value={form.preferred_intake} onChange={upd("preferred_intake")} className="input-dark" data-testid="ad-lead-intake">
          <option value="">— Select —</option>
          {intakeOptions.map((i) => (
            <option key={i} value={i}>{i}</option>
          ))}
        </select>
      </div>

      <div>
        <label className="text-xs uppercase tracking-widest text-white/55 mb-1.5 block">Approximate Annual Tuition Budget *</label>
        <select required value={form.tuition_budget} onChange={upd("tuition_budget")} className="input-dark" data-testid="ad-lead-budget">
          <option value="">— Select —</option>
          {TUITION_BUDGET_OPTIONS.map((b) => (
            <option key={b} value={b}>{b}</option>
          ))}
        </select>
      </div>

      <button type="submit" disabled={submitting} className="btn-gold w-full justify-center" data-testid="ad-lead-submit">
        {submitting ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
        Get My Free Plan
      </button>
    </form>
  );
}
